import React, { useMemo, useState } from "react";
import { Stack, FontIcon } from "@fluentui/react";
import SummaryTable from "./SummaryTable";
import { IBrand, IReport_Merchant, IReport_ProductItem, IReport_Receipt } from "../../../shared/interfaces/IReceipAnalyzerEntities";
import { useAppContext } from "./AppContext";
import { useSearchParams } from "react-router";
import { useNavigate } from 'react-router';
import ActionButton from './ActionButton';
import ProgressBar from './ProgressBar';
import SConsciousApiService from "../services/SConsciousApiService";
import {
    ResponsiveContainer, ComposedChart, Line, Area,
    XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const TOOLTIP_STYLE = {
    contentStyle: {
        background: '#111',
        border: '1px solid rgba(255,204,0,0.3)',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: '0.72rem',
    },
    itemStyle: { color: '#fff' },
    labelStyle: { color: '#ffcc00', fontWeight: 700 },
} as const;

const AXIS_TICK = { fontSize: 10, fontFamily: 'inherit', fill: '#000' };

const _api = new SConsciousApiService();

export default function Brand() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {appData, setAppData} = useAppContext(); // <-- get context method

    const brandId = searchParams.get("brandId");
    const brand = appData.data.brands?.find((m) => m.name === brandId);

    const productItems = appData.data.reportProductItems.filter(item => item.brand === brandId);

    // Corporation editing state
    const [showCorpEditor, setShowCorpEditor] = useState(false);
    const [corpSuggestions, setCorpSuggestions] = useState<string[]>([]);
    const [corpLoading, setCorpLoading] = useState(false);
    const [selectedCorp, setSelectedCorp] = useState<string | null>(null);
    const [corpSaving, setCorpSaving] = useState(false);
    const [corpError, setCorpError] = useState<string | null>(null);
    const [corpRewardMessage, setCorpRewardMessage] = useState<string | null>(null);
    const [localCorporation, setLocalCorporation] = useState<string | undefined>(undefined);
    const displayCorporation = localCorporation ?? brand?.owning_corporation;

    // Group by description, sum quantity and total_price
    const uniqueProducts = Object.values(
        productItems.reduce((acc, item) => {
            const key = item.product_identifier ?? '';
            if (!acc[key]) {
                acc[key] = { ...item, quantity: 0, total_price: 0 };
            }
            acc[key].quantity = (acc[key].quantity || 0) + (item.quantity || 0);
            acc[key].total_price = (acc[key].total_price || 0) + (item.total_price || 0);
            return acc;
        }, {} as Record<string, any>)
    );

    const currency = appData.user.currency || '';

    const chartData = useMemo(() => {
        if (productItems.length < 2) return [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        // Step 1: For each product, compute unit price per month it was purchased
        const productMonthPrice = new Map<string, Map<string, { spent: number; qty: number }>>();
        const allMonths = new Set<string>();
        const spendByMonth = new Map<string, number>();
        productItems.forEach(pi => {
            if (!pi.receipt_transaction_date || pi.total_price == null || !pi.quantity) return;
            const productKey = pi.product_identifier ?? pi.description ?? '';
            const ym = pi.receipt_transaction_date.substring(0, 7);
            allMonths.add(ym);
            if (!productMonthPrice.has(productKey)) productMonthPrice.set(productKey, new Map());
            const monthMap = productMonthPrice.get(productKey)!;
            const cur = monthMap.get(ym) || { spent: 0, qty: 0 };
            cur.spent += pi.total_price;
            cur.qty += pi.quantity;
            monthMap.set(ym, cur);
            spendByMonth.set(ym, (spendByMonth.get(ym) || 0) + pi.total_price);
        });

        // Step 2: Build sorted timeline and carry-forward last known price per product
        const sortedMonths = Array.from(allMonths).sort();
        const productPriceTimeline = new Map<string, Map<string, number>>();
        productMonthPrice.forEach((monthMap, productKey) => {
            const timeline = new Map<string, number>();
            let lastPrice: number | null = null;
            for (const ym of sortedMonths) {
                const data = monthMap.get(ym);
                if (data) lastPrice = data.spent / data.qty;
                if (lastPrice != null) timeline.set(ym, lastPrice);
            }
            productPriceTimeline.set(productKey, timeline);
        });

        // Step 3: Compute raw avg price per month, then normalise both to index 100
        const rows = sortedMonths.map(ym => {
            let sum = 0; let count = 0;
            productPriceTimeline.forEach(tl => { const p = tl.get(ym); if (p != null) { sum += p; count++; } });
            return {
                ym,
                rawPrice: count > 0 ? Math.round((sum / count) * 100) / 100 : 0,
                rawSpend: Math.round((spendByMonth.get(ym) || 0) * 100) / 100,
            };
        });

        const basePrice = rows.find(r => r.rawPrice)?.rawPrice || 1;
        const baseSpend = rows.find(r => r.rawSpend)?.rawSpend || 1;

        return rows.map(r => {
            const [year, month] = r.ym.split('-');
            return {
                month: `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`,
                avgPrice: Math.round((r.rawPrice / basePrice) * 100),
                spending: Math.round((r.rawSpend / baseSpend) * 100),
                _rawPrice: r.rawPrice,
                _rawSpend: r.rawSpend,
            };
        });
    }, [productItems]);

    const handleCorpEditToggle = async () => {
        if (showCorpEditor) { setShowCorpEditor(false); return; }
        setShowCorpEditor(true);
        setCorpError(null);
        setCorpRewardMessage(null);
        setSelectedCorp(null);
        const userId = appData?.user?.userId;
        if (!brandId || !userId) return;
        setCorpLoading(true);
        const suggestions = await _api.suggestCorporations(brandId, userId, displayCorporation);
        setCorpSuggestions(suggestions);
        setCorpLoading(false);
    };

    const handleCorpSave = async () => {
        if (!selectedCorp || !brandId) return;
        const userId = appData?.user?.userId;
        if (!userId) return;
        setCorpSaving(true);
        setCorpError(null);
        const ok = await _api.updateBrandCorporation(brandId, selectedCorp, userId);
        setCorpSaving(false);
        if (ok) {
            setLocalCorporation(selectedCorp);
            setShowCorpEditor(false);
            setCorpRewardMessage('+1 sCoN earned for updating corporation!');
            setAppData({ ...appData, loading: true });
        } else {
            setCorpError('Could not save. Please try again.');
        }
    };

    if (!appData.dataFetched) {
        return <ProgressBar label="Loading" progress={20} />;
    }

    if (!brand) {
        navigate('/');
        return null;
    }

    return (
        <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
            <div className="entity-summary">
                <br/>
                <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto', padding: '18px 0' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, margin: 6, letterSpacing: 1 }}>
                        {brand?.name || 'Unknown Brand'}
                    </div>
                    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <ActionButton label={displayCorporation || 'Unknown Corporation'} style={{ margin: 0 }} onClick={() => navigate(`/Corporation?corporationId=${displayCorporation}`)} />
                        <button
                            onClick={handleCorpEditToggle}
                            style={{ fontFamily: 'inherit', background: showCorpEditor ? '#000' : 'rgba(0,0,0,0.10)', color: showCorpEditor ? '#ffcc00' : '#000', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                            title="Edit corporation"
                        >
                            <FontIcon iconName="Edit" style={{ fontSize: '0.75rem' }} />
                        </button>
                    </div>
                    {corpRewardMessage && !showCorpEditor && (
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#000', background: '#ffcc00', borderRadius: 8, padding: '6px 10px', margin: '0 auto 8px', maxWidth: '80%' }}>
                            {corpRewardMessage}
                        </div>
                    )}
                    {showCorpEditor && (
                        <div style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 12, padding: '14px 16px', margin: '4px auto 8px', width: '90%', maxWidth: 340 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, opacity: 0.6 }}>
                                Select corporation for &ldquo;{brand?.name}&rdquo;
                            </div>
                            {corpLoading ? (
                                <div style={{ fontSize: '0.82rem', opacity: 0.55, marginBottom: 10 }}>Getting suggestions…</div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                    {corpSuggestions.map(c => (
                                        <button
                                            key={c}
                                            onClick={() => setSelectedCorp(c)}
                                            style={{
                                                fontFamily: 'inherit',
                                                background: selectedCorp === c ? '#000' : 'rgba(0,0,0,0.10)',
                                                color: selectedCorp === c ? '#ffcc00' : '#000',
                                                border: 'none',
                                                borderRadius: 20,
                                                padding: '6px 14px',
                                                fontSize: '0.82rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {corpError && (
                                <div style={{ fontSize: '0.78rem', color: '#e53935', marginBottom: 8 }}>{corpError}</div>
                            )}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button
                                    onClick={handleCorpSave}
                                    disabled={!selectedCorp || corpSaving}
                                    style={{
                                        fontFamily: 'inherit',
                                        flex: 1,
                                        background: '#000',
                                        color: '#ffcc00',
                                        border: 'none',
                                        borderRadius: 20,
                                        padding: '8px 0',
                                        opacity: corpSaving ? 0.35 : 1,
                                        cursor: selectedCorp && !corpSaving ? 'pointer' : 'not-allowed',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <FontIcon iconName="Save" style={{ fontSize: '1rem' }} />
                                </button>
                                <button
                                    onClick={() => setShowCorpEditor(false)}
                                    disabled={corpSaving}
                                    style={{
                                        fontFamily: 'inherit',
                                        flex: '0 0 calc(25% - 30px)',
                                        marginLeft: 16,
                                        background: 'rgba(0,0,0,0.06)',
                                        color: 'rgba(0,0,0)',
                                        border: 'none',
                                        borderRadius: 20,
                                        padding: '8px 0',
                                        cursor: corpSaving ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <FontIcon iconName="Cancel" style={{ fontSize: '1rem' }} />
                                </button>
                            </div>
                        </div>
                    )}
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                        <strong>Total:</strong> {productItems.reduce((sum, item) => sum + (item.total_price || 0), 0).toFixed(2)} {currency}
                    </div>
                </div>
                {chartData.length > 1 && (
                    <div style={{ width: '90%', margin: '12px auto 0' }}>
                        <div className="chart-card">
                            <span className="chart-title">
                                Trends
                                <span style={{ fontSize: '0.65rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.5 }}>
                                    {' '}— indexed to 100
                                </span>
                            </span>
                            <ResponsiveContainer width="100%" height={220}>
                                <ComposedChart data={chartData}>
                                    <defs>
                                        <linearGradient id="brandSpendGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#000" stopOpacity={0.12} />
                                            <stop offset="100%" stopColor="#000" stopOpacity={0.01} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                    <XAxis dataKey="month" tick={AXIS_TICK} />
                                    <YAxis tick={AXIS_TICK} />
                                    <Tooltip
                                        {...TOOLTIP_STYLE}
                                        formatter={((value: any, name: any, props: any) => {
                                            const d = props.payload;
                                            if (name === 'spending') return [`${currency} ${d._rawSpend?.toFixed(2) ?? '–'}  (${value})`, 'Spending'];
                                            if (name === 'avgPrice') return [`${currency} ${d._rawPrice?.toFixed(2) ?? '–'}  (${value})`, 'Avg Price'];
                                            return [value, name];
                                        }) as any}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '0.7rem', fontFamily: 'inherit' }} />
                                    <Area type="monotone" dataKey="spending" stroke="#000" strokeWidth={2} fill="url(#brandSpendGrad)" name="spending" connectNulls />
                                    <Line type="monotone" dataKey="avgPrice" stroke="#806000" strokeWidth={2} dot={{ r: 2, fill: '#806000' }} name="avgPrice" connectNulls />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
                <div>
                    <br/>
                    <strong style={{ fontSize: 14 }}>Product Items:</strong>
                    <br/>
                    <br/>
                    <Stack horizontalAlign="center" style={{ margin: 'auto', width: '90%' }}>
                    <SummaryTable
                        data={uniqueProducts.map((item, idx) => ({ id: idx, ...item }))}
                        columns={[
                        { 
                            key: "description", 
                            label: "Description", 
                            width: "50%", 
                            total: false, 
                            render: (_: any, item: IReport_ProductItem) => (
                                <span className='table-cell-content' onClick={() => navigate(`/ProductItem?receiptId=${encodeURIComponent(item.receipt_id)}&itemId=${encodeURIComponent(item.item_id)}`)} style={{ cursor: 'pointer' }}>{item.description}</span>
                            ),
                        },
                        { key: "quantity", label: "Quantity", width: "33%", total: true, render: (v: any) => Math.round(v) },
                        { key: "unit_price", label: "Price", width: "22%", total: true },
                        ]}
                        filterKeys={[]}
                        header={true}
                        total={true}
                    />
                    </Stack>
                    <br/>
                </div>
            </div>
        </Stack>
    );
}